import React, { useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Send, CheckCircle2, MessageCircle, X } from "lucide-react";
import { PRODUCT_OPTIONS, buildWhatsAppUrl, buildInquiryMessage } from "@/lib/brand";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function InquiryModal({ open, onOpenChange }) {
  const [form, setForm] = useState({
    full_name: "",
    mobile: "",
    email: "",
    product_requirement: "",
    notes: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleChange = (field) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((s) => ({ ...s, [field]: value }));
    setErrors((er) => ({ ...er, [field]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.full_name.trim() || form.full_name.trim().length < 2) {
      next.full_name = "Please enter your full name";
    }
    const mobileDigits = form.mobile.replace(/\D/g, "");
    // Accept exactly 10 digits, or 12 digits when prefixed with country code "91".
    const isTen = mobileDigits.length === 10;
    const isTenWith91 = mobileDigits.length === 12 && mobileDigits.startsWith("91");
    if (!isTen && !isTenWith91) {
      next.mobile = "Enter a valid 10-digit mobile number";
    } else if (isTen && !/^[6-9]/.test(mobileDigits)) {
      next.mobile = "Indian mobile numbers start with 6, 7, 8 or 9";
    } else if (isTenWith91 && !/^[6-9]/.test(mobileDigits.slice(2))) {
      next.mobile = "Indian mobile numbers start with 6, 7, 8 or 9";
    }
    if (!form.product_requirement) {
      next.product_requirement = "Please pick a requirement";
    }
    // Email is optional but, if provided, must look like an email address.
    const email = form.email.trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(email)) {
      next.email = "Enter a valid email address";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate() || submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form, {
        headers: { "Content-Type": "application/json" },
        timeout: 15000,
      });
      // Google Ads conversion tracking — counts a form-submit as a conversion.
      try {
        if (typeof window.gtag_report_conversion === "function") {
          window.gtag_report_conversion();
        }
      } catch (_) { /* ignore */ }
      setDone(true);
      toast.success("We've got your inquiry. Our team will reach out shortly.");
    } catch (err) {
      const message =
        err?.response?.data?.detail || "Couldn't submit right now. Please try again.";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  // Send via WhatsApp: programmatically click a transient anchor to open WA in a
  // new tab. Avoids the Chromium quirk where window.open(url, '_blank',
  // 'noopener,noreferrer') returns null and our previous popup-block detection
  // navigated the whole landing page away.
  const handleWhatsApp = () => {
    if (!validate() || submitting) return;
    const url = buildWhatsAppUrl(buildInquiryMessage(form));

    // Google Ads conversion tracking — counts a WhatsApp click as a conversion.
    try {
      if (typeof window.gtag_report_conversion === "function") {
        window.gtag_report_conversion();
      }
    } catch (_) { /* ignore */ }

    // 1) Open WhatsApp synchronously inside the click handler.
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();

    // 2) Persist the lead in the background.
    axios
      .post(
        `${API}/inquiries`,
        { ...form, source: "landing-page-whatsapp" },
        { headers: { "Content-Type": "application/json" }, timeout: 8000 }
      )
      .catch(() => {});

    // 3) Switch the modal to its success state.
    setDone(true);
    toast.success("Opening WhatsApp… complete your message there.");
  };

  const reset = () => {
    setForm({ full_name: "", mobile: "", email: "", product_requirement: "", notes: "" });
    setErrors({});
    setDone(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) setTimeout(reset, 200);
        onOpenChange(v);
      }}
    >
      <DialogContent
        data-testid="inquiry-modal"
        className="bg-white border border-zinc-200 text-zinc-950 rounded-3xl sm:max-w-[480px] p-0 max-h-[92svh] max-h-[92dvh] overflow-y-auto overscroll-contain shadow-2xl [&>button]:hidden"
      >
        {/* Sticky close-button bar — always visible at the top of the modal,
            even when the form scrolls. Fixes iOS/desktop "can't reach the X"
            because the modal exceeds the viewport height. */}
        <div className="sticky top-0 z-40 flex justify-end px-3 pt-3 pointer-events-none">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label="Close inquiry"
            data-testid="inquiry-close-x"
            className="pointer-events-auto inline-flex items-center justify-center w-11 h-11 rounded-full bg-white hover:bg-zinc-100 border border-zinc-200 text-zinc-700 shadow-md transition active:scale-95 touch-manipulation"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="relative -mt-14">
          <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-emerald-50 to-transparent pointer-events-none" />
          <DialogHeader className="relative px-5 pt-3 pb-1 text-left">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-emerald-700 mb-2">
              <MessageCircle className="w-3 h-3" /> Free Inquiry
            </div>
            <DialogTitle className="font-display text-xl md:text-2xl font-semibold tracking-tight text-zinc-950">
              {done ? "Inquiry received." : "Tell us what you need."}
            </DialogTitle>
            <DialogDescription className="text-zinc-600 text-xs md:text-sm">
              {done
                ? "Our team will reach out within one business day."
                : "Share your details — we'll send a customized shortlist."}
            </DialogDescription>
          </DialogHeader>

          {done ? (
            <div className="relative px-5 pb-5 pt-2">
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
                <div className="text-sm text-zinc-800">
                  Thanks{form.full_name ? `, ${form.full_name.split(" ")[0]}` : ""}. We&apos;ve logged your
                  request for <span className="text-emerald-700 font-semibold">{form.product_requirement}</span>.
                </div>
              </div>
              <button
                data-testid="inquiry-close"
                onClick={() => onOpenChange(false)}
                className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-zinc-100 hover:bg-zinc-200 text-zinc-900 rounded-full px-5 py-2.5 transition"
              >
                Close
              </button>
            </div>
          ) : (
            <form
              data-testid="inquiry-form"
              onSubmit={handleSubmit}
              className="relative px-5 pb-5 pt-2 space-y-2.5"
              noValidate
            >
              <div className="space-y-1">
                <Label htmlFor="iq-name" className="text-[10px] uppercase tracking-[0.16em] text-zinc-600">
                  Full Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="iq-name"
                  data-testid="inquiry-name"
                  value={form.full_name}
                  onChange={handleChange("full_name")}
                  placeholder="e.g. Aarav Mehta"
                  className="bg-white border-zinc-300 text-zinc-950 placeholder:text-zinc-400 rounded-xl h-10"
                  autoComplete="name"
                  required
                />
                {errors.full_name && (
                  <p className="text-[11px] text-red-600">{errors.full_name}</p>
                )}
              </div>

              <div className="space-y-1">
                <Label htmlFor="iq-mobile" className="text-[10px] uppercase tracking-[0.16em] text-zinc-600">
                  Mobile Number <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="iq-mobile"
                  data-testid="inquiry-mobile"
                  value={form.mobile}
                  onChange={(e) => {
                    let v = e.target.value.replace(/[^\d+\s]/g, "");
                    if (v.length > 14) v = v.slice(0, 14);
                    setForm((s) => ({ ...s, mobile: v }));
                    setErrors((er) => ({ ...er, mobile: undefined }));
                  }}
                  placeholder="10-digit mobile, e.g. 9876543210"
                  type="tel"
                  inputMode="tel"
                  pattern="[0-9+\s]{10,14}"
                  maxLength={14}
                  className="bg-white border-zinc-300 text-zinc-950 placeholder:text-zinc-400 rounded-xl h-10"
                  autoComplete="tel"
                  required
                />
                {errors.mobile && (
                  <p className="text-[11px] text-red-600">{errors.mobile}</p>
                )}
              </div>

              <div className="space-y-1">
                <Label htmlFor="iq-email" className="text-[10px] uppercase tracking-[0.16em] text-zinc-600">
                  Email <span className="text-zinc-400 normal-case tracking-normal">(optional)</span>
                </Label>
                <Input
                  id="iq-email"
                  data-testid="inquiry-email"
                  value={form.email}
                  onChange={handleChange("email")}
                  placeholder="you@company.com"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  className="bg-white border-zinc-300 text-zinc-950 placeholder:text-zinc-400 rounded-xl h-10"
                />
                {errors.email && (
                  <p className="text-[11px] text-red-600">{errors.email}</p>
                )}
              </div>

              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-600">
                  Product Requirement <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={form.product_requirement}
                  onValueChange={handleChange("product_requirement")}
                >
                  <SelectTrigger
                    data-testid="inquiry-product"
                    className="bg-white border-zinc-300 text-zinc-950 rounded-xl h-10"
                  >
                    <SelectValue placeholder="Pick what you need" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-zinc-200 text-zinc-950">
                    {PRODUCT_OPTIONS.map((opt) => (
                      <SelectItem
                        key={opt}
                        value={opt}
                        data-testid={`inquiry-option-${opt.replace(/[\s—/(),]+/g, "-").toLowerCase()}`}
                        className="focus:bg-emerald-50 focus:text-zinc-950"
                      >
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.product_requirement && (
                  <p className="text-[11px] text-red-600">{errors.product_requirement}</p>
                )}
              </div>

              <button
                type="submit"
                data-testid="inquiry-submit"
                disabled={submitting}
                className="mt-1 w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold rounded-full px-5 py-2.5 transition shadow-[0_10px_24px_rgba(16,185,129,0.3)]"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Submitting…
                  </>
                ) : (
                  <>
                    Submit Enquiry <Send className="w-4 h-4" />
                  </>
                )}
              </button>

              <button
                type="button"
                data-testid="inquiry-whatsapp"
                onClick={handleWhatsApp}
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 bg-white hover:bg-[#25D366]/10 text-[#075E54] font-semibold rounded-full px-5 py-2.5 border border-[#25D366]/40 hover:border-[#25D366] transition"
              >
                <svg viewBox="0 0 32 32" className="w-4 h-4" fill="currentColor" aria-hidden="true">
                  <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.032 1.318-1.04 2.264v.114c-.015.99.402 1.977 1.118 2.917 1.32 1.736 2.812 3.226 4.652 4.218.487.246.998.46 1.518.65.728.246 1.39.215 1.92.215.574 0 1.46-.187 1.92-.46.56-.337.84-.99.99-1.59.044-.214.044-.387.044-.572 0-.214-.014-.387-.043-.586-.044-.215-1.318-.864-1.677-.864zM16.04 5.726c-5.74 0-10.404 4.663-10.404 10.404 0 1.875.504 3.717 1.46 5.34l-1.917 5.755 5.92-1.89a10.336 10.336 0 0 0 4.94 1.243c5.74 0 10.404-4.663 10.404-10.404S21.78 5.726 16.04 5.726zm0 18.953a8.494 8.494 0 0 1-4.337-1.182l-.31-.184-3.215 1.027 1.044-3.13-.2-.32a8.55 8.55 0 0 1-1.31-4.555c0-4.722 3.842-8.563 8.563-8.563s8.563 3.84 8.563 8.563-3.84 8.563-8.563 8.563z"/>
                </svg>
                Send via WhatsApp
              </button>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
